import { useEffect, useMemo, useRef, useState } from "react";
import { boardBook, MARKET_KINDS, MARKET_LABEL, type Board, type MarketKind, type SideBook } from "../lib/board";
import { clusterPins, fmtTick, plotScale, priceAt, stepPath } from "../lib/chart";
import { cn } from "../lib/cn";
import { demoPoints, wantsDemoHistory, wantsNewsMockup } from "../lib/demo-history";
import { fmtOdds, fmtStart, sideLineText } from "../lib/format";
import {
  biggestMove,
  HISTORY_RANGES,
  historyWindow,
  sideSeries,
  type HistoryRange,
  type SideSeries,
} from "../lib/history";
import { useOddsFormat } from "../lib/odds-format";
import { chartPins, keyPin, type NewsPin } from "../lib/news";
import { isHistoryLocked, usePriceHistory, useTeamNews } from "../lib/queries";
import { cardFilename, gameUrl, type CardSeries, type ChartCard } from "../lib/share";
import type { Contest } from "../lib/types";
import { ShareButton } from "./ShareButton";

const TONES = ["accent", "accent-2"] as const;
const STROKE = { accent: "var(--color-accent)", "accent-2": "var(--color-accent-2)" };

/** "Eagles", "Eagles -3.5", "Over 41.5". */
function sideName(side: SideBook): string {
  if (side.line === null) return side.label;
  if (side.sideKey === "over" || side.sideKey === "under") return `${side.label} ${side.line}`;
  return `${side.label} ${sideLineText(side) ?? ""}`.trim();
}

/**
 * "How the line moved": the main line of one board market over time, one line per side, with the
 * open → now fact spelled out above it and a share button that turns it into a social card.
 */
export function PriceHistory({
  contest,
  board,
  title,
  league,
  teams,
}: {
  contest: Contest;
  board: Board;
  /** "Eagles @ Bears" */
  title: string;
  league?: string;
  /** Both teams' names, for the news pins. */
  teams: string[];
}) {
  const format = useOddsFormat();
  const kinds = MARKET_KINDS.filter((k) => board[k]);
  const [picked, setKind] = useState<MarketKind>("moneyline");
  const kind = kinds.includes(picked) ? picked : (kinds[0] ?? "moneyline");
  const [range, setRange] = useState<HistoryRange>("24h");

  const book = boardBook(board[kind]);
  // Two-way books chart both sides; a 3-way result charts the two teams, not the draw.
  const sides = useMemo(() => (book ? book.sides.filter((s) => s.sideKey !== "draw" && s.sideKey !== "tie").slice(0, 2) : []), [book]);
  const hashes = useMemo(() => sides.flatMap((s) => s.positionHashes ?? []), [sides]);

  const demo = wantsDemoHistory();
  const q = usePriceHistory(demo ? null : contest.id, hashes, range);
  const newsQ = useTeamNews(league, teams, wantsNewsMockup());
  const news = newsQ.data;
  const series: SideSeries[] = useMemo(() => {
    if (demo) {
      const window = historyWindow(range);
      // Jumps only at the headlines the chart will pin, so the sample reads like the real thing.
      const jumps = chartPins(news ?? [], window.from, window.to).map((n) => n.t);
      return sideSeries(demoPoints(sides, window, jumps), sides);
    }
    return q.data ? sideSeries(q.data.points, sides) : [];
  }, [demo, q.data, sides, range, news]);
  const drawn = series.filter((s) => s.points.length > 0);
  const times = drawn.flatMap((s) => s.points.map((p) => p.t));
  const pins = news && times.length ? chartPins(news, Math.min(...times), Math.max(...times)) : [];
  const key = keyPin(pins, drawn);
  const move = biggestMove(drawn);
  const locked = !demo && isHistoryLocked(q.error);
  const rangeLabel = { "24h": "Last 24 hours", "7d": "Last 7 days", all: "Since odds opened" }[range];

  const fmt = (price: number) => fmtOdds(price, null, format);
  const moveSide = move ? sides.find((s) => s.sideKey === move.series.sideKey) : null;
  const headline =
    move && moveSide
      ? Math.abs(move.move.chancePts) < 0.5
        ? `${sideName(moveSide)}: no change at ${fmt(move.move.now.price)}`
        : `${sideName(moveSide)}: ${fmt(move.move.open.price)} → ${fmt(move.move.now.price)}`
      : null;
  const sub =
    move && moveSide
      ? rangeLabel
      : null;

  const start = fmtStart(contest.starts_at);
  const eyebrow = [league, start.live ? "Live" : [start.day ?? "Today", start.time].join(" · ")]
    .filter(Boolean)
    .join(" · ");
  const url = gameUrl(contest.id);
  const card = (): ChartCard | null =>
    headline && sub
      ? {
          eyebrow,
          title,
          market: MARKET_LABEL[kind],
          headline,
          sub: `${sub} · Best odds across betting apps`,
          series: drawn.map<CardSeries>((s, i) => ({
            label: sideName(sides.find((x) => x.sideKey === s.sideKey) ?? sides[0]),
            points: s.points,
            tone: TONES[i % TONES.length],
          })),
          yLabel: fmt,
          link: url.replace(/^https?:\/\//, ""),
          pins: key ? [{ t: key.t }] : [],
          note: key ? `${fmtDay(key.t)} · ${key.headline} (${key.source})` : undefined,
        }
      : null;

  // No moneyline, spread or total on this game (props only): nothing to chart.
  if (!book) return null;

  return (
    <section className="mt-5 rounded-2xl border border-line bg-card/80 p-4 backdrop-blur-sm sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-bold">How the odds moved</h2>
          <p className="mt-0.5 text-[12px] text-dim">The best odds you could get, across every betting app.</p>
        </div>
        <ShareButton
          card={card}
          caption={
            headline
              ? `${title} · ${MARKET_LABEL[kind]}: ${headline} (${rangeLabel.toLowerCase()})` +
                (key ? `\nIn the news ${fmtDay(key.t)}: ${key.headline} (${key.source})` : "")
              : title
          }
          url={url}
          filename={cardFilename(title, MARKET_LABEL[kind])}
          disabled={!headline}
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <Segmented
          options={kinds.map((k) => ({ value: k, label: MARKET_LABEL[k] }))}
          value={kind}
          onChange={(v) => setKind(v as MarketKind)}
        />
        <Segmented
          options={HISTORY_RANGES.map((r) => ({ value: r.value, label: r.label }))}
          value={range}
          onChange={(v) => setRange(v as HistoryRange)}
        />
      </div>

      {locked ? (
        <Empty
          title="Odds history is coming soon"
          body="We're adding charts of how the odds moved. The odds on this page are live."
        />
      ) : !demo && q.isPending && q.fetchStatus !== "idle" ? (
        <div className="skeleton mt-4 h-64 rounded-xl" />
      ) : q.isError && !demo ? (
        <Empty title="Couldn't load the odds history" body="Try again in a minute." />
      ) : drawn.length === 0 ? (
        <Empty title="No odds moves yet" body={`These odds haven't moved in the ${rangeLabel.toLowerCase()}.`} />
      ) : (
        <>
          {headline && (
            <div className="mt-4">
              <div className="brand-text font-display text-xl font-extrabold sm:text-2xl">{headline}</div>
              {sub && <div className="mt-0.5 text-[12px] text-dim">{sub}</div>}
            </div>
          )}
          <Chart series={drawn} sides={sides} fmt={fmt} pins={pins} />
          {pins.length > 0 && <NewsList pins={pins} />}
          {demo && <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-live">Sample data (dev only)</p>}
        </>
      )}
    </section>
  );
}

/** "Sep 26, 2:30 PM" */
function fmtDay(t: number): string {
  return new Date(t).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** The headlines behind the chart's numbered pins. Timing only — no claim about cause. */
function NewsList({ pins }: { pins: NewsPin[] }) {
  return (
    <div className="mt-4 border-t border-line pt-4">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[13px] font-bold">In the news</h3>
        <span className="text-[11px] text-faint">Headlines from the same time as the chart</span>
      </div>
      <ol className="flex flex-col gap-1">
        {[...pins].reverse().map((p) => (
          <li key={p.id}>
            <a
              href={p.url ?? undefined}
              target="_blank"
              rel="noreferrer"
              className="flex items-start gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-cell"
            >
              <PinBadge n={p.n} />
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold leading-snug">{p.headline}</span>
                <span className="text-[11px] text-dim">
                  {p.source} · {fmtDay(p.t)}
                </span>
              </span>
            </a>
          </li>
        ))}
      </ol>
    </div>
  );
}

function PinBadge({ n }: { n: number }) {
  return (
    <span className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-dim bg-panel text-[10px] font-bold text-ink">
      {n}
    </span>
  );
}

function Chart({
  series,
  sides,
  fmt,
  pins,
}: {
  series: SideSeries[];
  sides: SideBook[];
  fmt: (p: number) => string;
  pins: NewsPin[];
}) {
  const wrap = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(640);
  const [hoverT, setHoverT] = useState<number | null>(null);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const height = width < 520 ? 220 : 280;
  const top = pins.length ? 30 : 12;
  const box = { x: 52, y: top, w: width - 52 - 12, h: height - top - 28 };
  const plot = plotScale(series, box);
  if (!plot) return null;
  const span = plot.t1 - plot.t0;

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const f = Math.min(1, Math.max(0, (e.clientX - r.left - box.x) / box.w));
    setHoverT(plot.t0 + f * span);
  };

  return (
    <div ref={wrap} className="relative mt-3">
      {/* Legend */}
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1">
        {series.map((s, i) => {
          const side = sides.find((x) => x.sideKey === s.sideKey);
          const last = s.points[s.points.length - 1];
          return (
            <span key={s.sideKey} className="flex items-center gap-2 text-[12px]">
              <span className="h-1 w-4 rounded-full" style={{ background: STROKE[TONES[i % 2]] }} />
              <span className="font-semibold">{side ? sideName(side) : s.label}</span>
              <span className="text-dim">now {fmt(last.price)}</span>
            </span>
          );
        })}
      </div>

      <svg
        width={width}
        height={height}
        className="block touch-none select-none"
        onPointerMove={onMove}
        onPointerLeave={() => setHoverT(null)}
        role="img"
        aria-label="Price history chart"
      >
        {plot.yTicks.map((v) => (
          <g key={v}>
            <line x1={box.x} x2={box.x + box.w} y1={plot.y(v)} y2={plot.y(v)} stroke="var(--color-line)" />
            <text x={box.x - 8} y={plot.y(v) + 4} textAnchor="end" fontSize="11" fill="var(--color-faint)">
              {fmt(v)}
            </text>
          </g>
        ))}
        {plot.xTicks.map((t, i) => (
          <text
            key={t}
            x={plot.x(t)}
            y={height - 6}
            textAnchor={i === 0 ? "start" : i === plot.xTicks.length - 1 ? "end" : "middle"}
            fontSize="11"
            fill="var(--color-faint)"
          >
            {fmtTick(t, span)}
          </text>
        ))}
        {series.map((s, i) => {
          const color = STROKE[TONES[i % 2]];
          const last = s.points[s.points.length - 1];
          return (
            <g key={s.sideKey}>
              <path
                d={stepPath(s.points, plot)}
                fill="none"
                stroke={color}
                strokeWidth="2.25"
                strokeLinejoin="round"
                style={{ filter: `drop-shadow(0 0 6px ${color})` }}
              />
              <circle cx={plot.x(last.t)} cy={plot.y(last.price)} r="4" fill={color} />
            </g>
          );
        })}
        {clusterPins(pins, plot.x, 26).map((g) => {
          const w = g.pins.length > 1 ? 30 : 18;
          return (
            <g key={g.pins[0].id}>
              <title>{g.pins.map((p) => `${p.n}. ${p.headline} (${p.source})`).join("\n")}</title>
              <line x1={g.x} x2={g.x} y1={box.y - 4} y2={box.y + box.h} stroke="var(--color-dim)" strokeOpacity="0.35" strokeDasharray="2 4" />
              <rect x={g.x - w / 2} y={box.y - 23} width={w} height="18" rx="9" fill="var(--color-panel)" stroke="var(--color-dim)" />
              <text x={g.x} y={box.y - 10.5} textAnchor="middle" fontSize="10" fontWeight="700" fill="var(--color-ink)">
                {g.label}
              </text>
            </g>
          );
        })}
        {hoverT !== null && (
          <g>
            <line
              x1={plot.x(hoverT)}
              x2={plot.x(hoverT)}
              y1={box.y}
              y2={box.y + box.h}
              stroke="var(--color-dim)"
              strokeDasharray="3 3"
            />
            {series.map((s, i) => {
              const p = priceAt(s.points, hoverT);
              return p ? (
                <circle key={s.sideKey} cx={plot.x(hoverT)} cy={plot.y(p.price)} r="4" fill={STROKE[TONES[i % 2]]} />
              ) : null;
            })}
          </g>
        )}
      </svg>

      {hoverT !== null && (
        <div
          className="pointer-events-none absolute top-8 rounded-xl border border-line bg-panel/95 px-3 py-2 text-[12px] shadow-lg"
          style={
            plot.x(hoverT) > width / 2
              ? { right: width - plot.x(hoverT) + 12 }
              : { left: plot.x(hoverT) + 12 }
          }
        >
          <div className="mb-1 text-[11px] text-faint">
            {new Date(hoverT).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </div>
          {series.map((s, i) => {
            const p = priceAt(s.points, hoverT);
            const side = sides.find((x) => x.sideKey === s.sideKey);
            return p ? (
              <div key={s.sideKey} className="flex items-center gap-2 whitespace-nowrap">
                <span className="h-1 w-3 rounded-full" style={{ background: STROKE[TONES[i % 2]] }} />
                <span className="text-dim">{side ? sideName(side) : s.label}</span>
                <span className="ml-auto font-bold">{fmt(p.price)}</span>
              </div>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
}

function Segmented({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex gap-1 rounded-full border border-line bg-panel/70 p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={cn(
            "rounded-full px-3 py-1 text-[12px] font-semibold transition-all",
            value === o.value ? "brand-gradient text-accent-ink" : "text-dim hover:text-ink",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="mt-4 flex h-56 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-line text-center">
      <div className="font-display text-base font-bold">{title}</div>
      <p className="max-w-sm px-6 text-[12px] text-dim">{body}</p>
    </div>
  );
}
